import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Card, Typography, Tabs, Tab, Stack, Button, IconButton,
  CircularProgress, TextField, Chip, Grid, Dialog, DialogTitle, DialogContent, DialogActions 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getContentById, updateContent, approveContent, regenerateContent, publishNow, scheduleContent, generateImageForChannel } from '../../../../services/contentApiFn';
import { handleOpenSnackBar } from '../../../../utils';
import RichTextEditor from '../../../../components/TextEditor';
import GlobalButton from '../../../../components/CustomButton';
import PaperContainer from '../../../../components/Containers/PaperContainer';
import NavigationDrawer from '../../../../components/NavigationDrawer';
import { FacebookPreview, InstagramPreview, BlogPreview, EmailPreview } from '../generate/ChannelPreviews';

function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState(null);
  
  // UI State
  const [activeTab, setActiveTab] = useState(0);
  const [lang, setLang] = useState('en');
  const [editData, setEditData] = useState(null);
  
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const res = await getContentById(id);
      if (res?.data?.status === 'success') {
        setPost(res.data.data);
        setEditData(JSON.parse(JSON.stringify(res.data.data.channels)));
      } else {
        handleOpenSnackBar('error', 'Failed to load content post');
        navigate('/content-hub');
      }
    } catch (error) {
      console.error(error);
      handleOpenSnackBar('error', 'Error loading content post');
      navigate('/content-hub');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await updateContent(id, { channels: editData });
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', 'Changes saved successfully');
        setPost(res.data.data);
      }
    } catch (error) {
      handleOpenSnackBar('error', 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      setSaving(true);
      const res = await approveContent(id);
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', 'Content approved');
        setPost(res.data.data);
      }
    } catch (error) {
      handleOpenSnackBar('error', 'Failed to approve');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = async () => {
    try {
      setSaving(true);
      const res = await publishNow(id);
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', 'Content published successfully');
        setPost(res.data.data.post);
      }
    } catch (error) {
      handleOpenSnackBar('error', error?.response?.data?.message || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate) return handleOpenSnackBar('error', 'Please select a valid date/time');
    try {
      setSaving(true);
      const res = await scheduleContent(id, { scheduledAt: scheduleDate });
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', 'Content scheduled successfully');
        setPost(res.data.data);
        setScheduleDialogOpen(false);
      }
    } catch (error) {
      handleOpenSnackBar('error', error?.response?.data?.message || 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateChannel = async (channel) => {
    try {
      setSaving(true);
      const res = await regenerateContent(id, { channels: [channel] });
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', `${channel} regenerated successfully`);
        setPost(res.data.data);
        // Update edit data for the regenerated channel
        setEditData(prev => ({
          ...prev,
          [channel]: res.data.data.channels[channel]
        }));
      }
    } catch (error) {
      handleOpenSnackBar('error', `Failed to regenerate ${channel}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateImage = async (channel) => {
    try {
      setSaving(true);
      const res = await generateImageForChannel(id, { channel });
      if (res?.data?.status === 'success') {
        handleOpenSnackBar('success', 'Image regenerated successfully');
        setPost(res.data.data);
        setEditData(prev => ({
          ...prev,
          [channel]: res.data.data.channels[channel]
        }));
      }
    } catch (error) {
      handleOpenSnackBar('error', 'Failed to regenerate image');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (channel, field, value) => {
    setEditData(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [field]: {
          ...prev[channel][field],
          [lang]: value
        }
      }
    }));
  };
  
  const handleStringChange = (channel, field, value) => {
    // For non-localized fields like suggestedImage
    setEditData(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!post) return null;

  const channelsList = [
    { key: 'facebook', label: 'Facebook' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'blog', label: 'Blog' },
    { key: 'email', label: 'Email' },
  ];

  const currentChannel = channelsList[activeTab].key;

  const productTitle = typeof post.product?.name === 'object'
    ? (post.product?.name?.en || post.product?.name?.ro || 'Product')
    : (post.product?.name || 'Product');

  return (
    <NavigationDrawer>
      <Box p={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
            <IconButton onClick={() => navigate('/content-hub')} sx={{ bgcolor: '#f0f2f5', '&:hover': { bgcolor: '#e4e6eb' }, flexShrink: 0 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight="bold" noWrap title={productTitle}>
                Content: {productTitle}
              </Typography>
              <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                <Chip 
                  label={post.status} 
                  size="small"
                  color={post.status === 'approved' ? 'primary' : post.status === 'scheduled' ? 'warning' : post.status === 'published' ? 'success' : 'default'}
                  sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                />
                <Typography variant="body2" color="textSecondary">
                  {new Date(post.createdAt).toLocaleString()}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0, alignItems: 'center' }}>
            {post.status === 'draft' && (
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleSave} 
                disabled={saving}
                sx={{ textTransform: 'none', fontWeight: 600, px: 2.5, whiteSpace: 'nowrap' }}
              >
                Save Draft
              </Button>
            )}
            {post.status === 'draft' && (
              <Button 
                variant="contained" 
                sx={{ bgcolor: '#7922D8', '&:hover': { bgcolor: '#5e19a8' }, textTransform: 'none', fontWeight: 600, px: 2.5, whiteSpace: 'nowrap' }} 
                onClick={handleApprove} 
                disabled={saving}
              >
                Approve Content
              </Button>
            )}
            
            {(post.status === 'approved' || post.status === 'scheduled') && (
              <>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => setScheduleDialogOpen(true)} 
                  disabled={saving}
                  sx={{ textTransform: 'none', fontWeight: 600, px: 2.5, whiteSpace: 'nowrap' }}
                >
                  {post.status === 'scheduled' ? 'Reschedule' : 'Schedule'}
                </Button>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={handlePublishNow} 
                  disabled={saving}
                  sx={{ textTransform: 'none', fontWeight: 600, px: 2.5, whiteSpace: 'nowrap' }}
                >
                  Publish Now
                </Button>
              </>
            )}
          </Stack>
        </Stack>

        {/* Editor Section */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, val) => setActiveTab(val)}
                  sx={{ 
                    '& .MuiTab-root': { 
                      textTransform: 'none', 
                      fontWeight: 600, 
                      fontSize: '0.95rem',
                      minHeight: '48px'
                    } 
                  }}
                >
                  {channelsList.map((ch) => (
                    <Tab key={ch.key} label={ch.label} />
                  ))}
                </Tabs>
                
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button 
                    variant={lang === 'en' ? 'contained' : 'outlined'} 
                    size="small" 
                    onClick={() => setLang('en')}
                    sx={{ 
                      borderRadius: '20px', 
                      minWidth: '46px', 
                      px: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      ...(lang === 'en' ? { bgcolor: '#7922D8', '&:hover': { bgcolor: '#5e19a8' } } : { color: '#7922D8', borderColor: '#7922D8' })
                    }}
                  >
                    EN
                  </Button>
                  <Button 
                    variant={lang === 'ro' ? 'contained' : 'outlined'} 
                    size="small" 
                    onClick={() => setLang('ro')}
                    sx={{ 
                      borderRadius: '20px', 
                      minWidth: '46px', 
                      px: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      ...(lang === 'ro' ? { bgcolor: '#7922D8', '&:hover': { bgcolor: '#5e19a8' } } : { color: '#7922D8', borderColor: '#7922D8' })
                    }}
                  >
                    RO
                  </Button>
                </Stack>
              </Box>

              <Box p={3} bgcolor="#fafafa" minHeight="500px">
                <Stack direction="row" justifyContent="flex-end" spacing={2} mb={2}>
                  {(currentChannel === 'facebook' || currentChannel === 'instagram') && (
                    <Button size="small" variant="outlined" color="secondary" onClick={() => handleRegenerateImage(currentChannel)} disabled={saving}>
                      🔄 Regenerate Image
                    </Button>
                  )}
                  <Button size="small" variant="text" onClick={() => handleRegenerateChannel(currentChannel)} disabled={saving}>
                    🔄 Regenerate {channelsList[activeTab].label}
                  </Button>
                </Stack>

                {/* Dynamic Editor based on channel */}
                {currentChannel === 'facebook' && (
                  <Stack spacing={3}>
                    <TextField
                      label="Post Text"
                      multiline
                      rows={6}
                      fullWidth
                      value={editData.facebook?.text?.[lang] || ''}
                      onChange={(e) => handleChange('facebook', 'text', e.target.value)}
                    />
                    <TextField
                      label="Suggested Image / Video URL"
                      fullWidth
                      value={editData.facebook?.suggestedImage || ''}
                      onChange={(e) => handleStringChange('facebook', 'suggestedImage', e.target.value)}
                    />
                    <TextField
                      label="Call to Action"
                      fullWidth
                      value={editData.facebook?.cta?.[lang] || ''}
                      onChange={(e) => handleChange('facebook', 'cta', e.target.value)}
                    />
                  </Stack>
                )}

                {currentChannel === 'instagram' && (
                  <Stack spacing={3}>
                    <TextField
                      label="Caption"
                      multiline
                      rows={6}
                      fullWidth
                      value={editData.instagram?.caption?.[lang] || ''}
                      onChange={(e) => handleChange('instagram', 'caption', e.target.value)}
                    />
                    <TextField
                      label="Hashtags (comma separated)"
                      fullWidth
                      value={editData.instagram?.hashtags?.[lang]?.join(', ') || ''}
                      onChange={(e) => {
                        const val = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                        handleChange('instagram', 'hashtags', val);
                      }}
                    />
                    <TextField
                      label="Suggested Image URL"
                      fullWidth
                      value={editData.instagram?.suggestedImage || ''}
                      onChange={(e) => handleStringChange('instagram', 'suggestedImage', e.target.value)}
                    />
                  </Stack>
                )}

                {currentChannel === 'blog' && (
                  <Stack spacing={3}>
                    <TextField
                      label="Title"
                      fullWidth
                      value={editData.blog?.title?.[lang] || ''}
                      onChange={(e) => handleChange('blog', 'title', e.target.value)}
                    />
                    <TextField
                      label="Small Description"
                      multiline
                      rows={2}
                      fullWidth
                      value={editData.blog?.smallDescription?.[lang] || ''}
                      onChange={(e) => handleChange('blog', 'smallDescription', e.target.value)}
                    />
                    <Box>
                      <Typography variant="subtitle2" mb={1} color="textSecondary">Blog Body (HTML)</Typography>
                      <RichTextEditor
                        id="blog-body-editor"
                        value={editData.blog?.body?.[lang] || ''}
                        onChange={(data) => handleChange('blog', 'body', data)}
                      />
                    </Box>
                  </Stack>
                )}

                {currentChannel === 'email' && (
                  <Stack spacing={3}>
                    <TextField
                      label="Subject Line"
                      fullWidth
                      value={editData.email?.subject?.[lang] || ''}
                      onChange={(e) => handleChange('email', 'subject', e.target.value)}
                    />
                    <TextField
                      label="Preview Text"
                      fullWidth
                      value={editData.email?.previewText?.[lang] || ''}
                      onChange={(e) => handleChange('email', 'previewText', e.target.value)}
                    />
                    <Box>
                      <Typography variant="subtitle2" mb={1} color="textSecondary">Email Body (HTML)</Typography>
                      <RichTextEditor
                        id="email-body-editor"
                        value={editData.email?.body?.[lang] || ''}
                        onChange={(data) => handleChange('email', 'body', data)}
                      />
                    </Box>
                  </Stack>
                )}

              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box mb={4}>
              {currentChannel === 'facebook' && <FacebookPreview content={editData.facebook} lang={lang} />}
              {currentChannel === 'instagram' && <InstagramPreview content={editData.instagram} lang={lang} />}
              {currentChannel === 'blog' && <BlogPreview content={editData.blog} lang={lang} />}
              {currentChannel === 'email' && <EmailPreview content={editData.email} lang={lang} />}
            </Box>

            <Card sx={{ p: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>Original Configuration</Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Languages</Typography>
                  <Typography variant="body2">{post.config?.languages?.join(', ')}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Tone</Typography>
                  <Typography variant="body2" textTransform="capitalize">{post.config?.tone}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Target Audience</Typography>
                  <Typography variant="body2">{post.config?.targetAudience || 'General'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Hashtag Style</Typography>
                  <Typography variant="body2" textTransform="capitalize">{post.config?.hashtagStyle}</Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Content</DialogTitle>
        <DialogContent>
          <Box pt={2}>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Select the date and time when this content should automatically be published to the selected channels.
            </Typography>
            <TextField
              fullWidth
              type="datetime-local"
              label="Publish At"
              InputLabelProps={{ shrink: true }}
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSchedule} variant="contained" color="primary">
            Confirm Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </NavigationDrawer>
  );
}

export default ContentDetail;
